import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import { LogoWinarq } from "../components/LogoWinarq";
import { useAuth } from "../contexts/AuthContext";

const O="#D2611A", BK="#1C1917";
const W={bg:"#FAF9F7",s1:"#FFFFFF",s2:"#F4F2EF",border:"#E8E4DE",off:"#3D3830",muted:"#8C8078",
  sans:"'Outfit','Helvetica Neue',Arial,sans-serif",mono:"'IBM Plex Mono',monospace"};
const RUBROS=["Sanitarios","Bañeras","Vanitorys","Pisos SPC","Hornos y Anafes","Puertas","Carpintería","Cocinas","Carpintería"];

// Rubros que son "a medida"
const RUBROS_MEDIDA=["Carpintería","Cocinas","Barandas y Balcones"];

const FONTS=`
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@300;400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
select,select option{background:#FFFFFF!important;color:#1C1917!important;-webkit-appearance:menulist!important}
@media(max-width:768px){
  .cat-layout{flex-direction:column!important;padding:12px!important}
  .cat-sidebar{width:100%!important;margin-bottom:10px!important;display:flex!important;flex-wrap:wrap!important;gap:6px!important}
  .cat-sidebar-lbl{display:none!important}
  .cat-grid{grid-template-columns:repeat(2,1fr)!important;gap:10px!important}
  .cat-nav{padding:0 12px!important;height:50px!important;gap:8px!important}
  .cat-chips{overflow-x:auto!important;flex-wrap:nowrap!important;-webkit-overflow-scrolling:touch!important}
  .cat-chip{flex-shrink:0!important;white-space:nowrap!important}
  .det-grid{grid-template-columns:1fr!important}
  .slider-track{gap:10px!important}
}
@media(max-width:480px){.cat-grid{grid-template-columns:1fr!important}}
`;

const WAMSG = (nombre) => encodeURIComponent(
  `Hola, me interesa consultar sobre: ${nombre}. También me gustaría conocer otros modelos y colores disponibles. ¿Podrían enviarme información?`
);

// ── Slider de fotos ───────────────────────────────────────────────────────────
function SliderFotos({ fotos, nombre }) {
  const [idx, setIdx] = useState(0);
  const touchStart = useRef(null);

  const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + fotos.length) % fotos.length); };
  const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % fotos.length); };

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next(e) : prev(e);
    touchStart.current = null;
  };

  if (!fotos.length) return (
    <div style={{width:"100%",height:320,background:W.s2,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:W.sans,fontSize:11,color:W.muted}}>Sin imagen</div>
  );

  return (
    <div style={{position:"relative",background:W.s2,overflow:"hidden"}} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Imagen principal — contain para no recortar */}
      <div style={{width:"100%",height:320,display:"flex",alignItems:"center",justifyContent:"center",background:W.s2}}>
        <img src={fotos[idx]} alt={nombre} style={{maxWidth:"100%",maxHeight:320,objectFit:"contain",display:"block"}}/>
      </div>
      {fotos.length > 1 && (
        <>
          <button onClick={prev} style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",background:"rgba(28,25,23,.6)",color:"#fff",border:"none",width:32,height:32,cursor:"pointer",fontSize:18,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
          <button onClick={next} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"rgba(28,25,23,.6)",color:"#fff",border:"none",width:32,height:32,cursor:"pointer",fontSize:18,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
          {/* Dots */}
          <div style={{position:"absolute",bottom:8,left:0,right:0,display:"flex",justifyContent:"center",gap:5}}>
            {fotos.map((_,i)=>(
              <div key={i} onClick={e=>{e.stopPropagation();setIdx(i);}} style={{width:i===idx?18:6,height:6,background:i===idx?O:"rgba(255,255,255,.6)",borderRadius:3,cursor:"pointer",transition:"width .3s"}}/>
            ))}
          </div>
        </>
      )}
      {/* Thumbnails */}
      {fotos.length > 1 && (
        <div className="slider-track" style={{display:"flex",gap:6,padding:"8px 10px",overflowX:"auto",background:"rgba(28,25,23,.04)"}}>
          {fotos.map((url,i)=>(
            <img key={i} src={url} alt="" onClick={e=>{e.stopPropagation();setIdx(i);}}
              style={{width:48,height:48,objectFit:"contain",background:W.s1,cursor:"pointer",border:i===idx?`2px solid ${O}`:`1px solid ${W.border}`,flexShrink:0,borderRadius:2}}/>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Modal detalle ─────────────────────────────────────────────────────────────
function DetalleProducto({producto, onClose, puedeVerPrecios}) {
  const navigate = useNavigate();
  useEffect(() => {
    const y = window.scrollY;
    document.body.style.cssText = `overflow:hidden;position:fixed;width:100%;top:-${y}px`;
    return () => { document.body.style.cssText = ""; window.scrollTo(0, y); };
  }, []);

  const fotos = [producto.imagen_url, ...(producto.imagenes || []).filter(u => u && u !== producto.imagen_url)].filter(Boolean);
  const bullets = (producto.caracteristicas || "").split("\n").map(l => l.replace(/^[•\-\*]\s*/, "").trim()).filter(Boolean);
  const precio = producto.precio_venta;
  const precioIVA = precio ? (precio * 1.21).toFixed(2) : null;
  const esMedida = RUBROS_MEDIDA.includes(producto.rubro);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(28,25,23,.72)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:12}} onClick={onClose}>
      <div className="det-grid" style={{background:W.s1,width:"100%",maxWidth:900,maxHeight:"92vh",overflowY:"auto",display:"grid",gridTemplateColumns:"1fr 1fr"}} onClick={e=>e.stopPropagation()}>

        {/* Galería con slider */}
        <SliderFotos fotos={fotos} nombre={producto.nombre}/>

        {/* Info */}
        <div style={{padding:20,display:"flex",flexDirection:"column",gap:12,overflowY:"auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div style={{flex:1,paddingRight:8}}>
              <div style={{fontFamily:W.sans,fontSize:8,color:O,letterSpacing:2,textTransform:"uppercase",marginBottom:3,fontWeight:600}}>{producto.rubro}</div>
              <div style={{fontFamily:W.sans,fontSize:"clamp(14px,3vw,18px)",color:BK,fontWeight:700,lineHeight:1.2}}>{producto.nombre}</div>
              {producto.sku && <div style={{fontFamily:W.mono,fontSize:9,color:W.muted,marginTop:2}}>{producto.sku}</div>}
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:22,color:W.muted,cursor:"pointer",padding:"0 0 0 8px"}}>×</button>
          </div>

          {/* Badge a medida */}
          {esMedida && (
            <div style={{background:"#EFF6FF",border:"1px solid #2563EB33",padding:"10px 14px"}}>
              <div style={{fontFamily:W.sans,fontSize:11,color:"#1D4ED8",fontWeight:700,marginBottom:3}}>✦ Diseño a medida del cliente</div>
              <div style={{fontFamily:W.sans,fontSize:10,color:"#3730A3",lineHeight:1.5}}>
                Trabajamos en cooperación directa con la fábrica. Podemos fabricar cualquier medida, color o diseño. Enviá tus planos y te preparamos un presupuesto.
              </div>
            </div>
          )}

          {/* Precio */}
          {puedeVerPrecios ? (
            precioIVA ? (
              <div style={{background:"#FEF3E8",border:`1.5px solid ${O}`,padding:12}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  <div>
                    <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:W.muted,marginBottom:3,fontWeight:600}}>Sin IVA</div>
                    <div style={{fontFamily:W.mono,fontSize:16,color:BK,fontWeight:700}}>USD {Number(precio).toLocaleString("es-AR",{minimumFractionDigits:2})} / {producto.unidad}</div>
                  </div>
                  <div>
                    <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:O,marginBottom:3,fontWeight:600}}>Con IVA</div>
                    <div style={{fontFamily:W.mono,fontSize:16,color:O,fontWeight:700}}>USD {Number(precioIVA).toLocaleString("es-AR",{minimumFractionDigits:2})}</div>
                  </div>
                </div>
                <div style={{fontFamily:W.sans,fontSize:10,color:W.muted,lineHeight:1.5}}>
                  ⚠ Precio orientativo — varía según flete vigente (USD 3.800–9.200 / contenedor 40HC)
                </div>
              </div>
            ) : (
              <div style={{background:W.s2,border:`1px solid ${W.border}`,padding:12,fontFamily:W.sans,fontSize:11,color:W.off}}>
                {esMedida ? "Precio según medidas y especificaciones del cliente." : "Precio a cotizar según especificaciones."}
              </div>
            )
          ) : (
            <div style={{background:"#FEF3E8",border:`1.5px solid ${O}`,padding:16,textAlign:"center"}}>
              <div style={{fontFamily:W.sans,fontSize:13,color:BK,fontWeight:700,marginBottom:6}}>Precios para clientes registrados</div>
              <div style={{fontFamily:W.sans,fontSize:11,color:W.muted,marginBottom:14,lineHeight:1.6}}>
                Registrate para ver precios y cotizar tu obra.
              </div>
              <button onClick={()=>navigate("/auth")} style={{padding:"10px 24px",background:O,color:"#fff",fontFamily:W.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",border:"none",cursor:"pointer",fontWeight:700}}>Ver precios →</button>
            </div>
          )}

          {/* Specs */}
          {bullets.length > 0 && (
            <div>
              <div style={{fontFamily:W.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:W.muted,marginBottom:8,fontWeight:600}}>Especificaciones</div>
              {bullets.map((b,i) => (
                <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"5px 0",borderBottom:`1px solid ${W.border}`}}>
                  <span style={{color:O,fontWeight:700,flexShrink:0,fontSize:11}}>→</span>
                  <span style={{fontFamily:W.sans,fontSize:11,color:W.off}}>{b}</span>
                </div>
              ))}
            </div>
          )}

          {/* Botones */}
          <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:"auto"}}>
            <a href={`https://wa.me/5491150614489?text=${WAMSG(producto.nombre)}`} target="_blank" rel="noreferrer"
              style={{display:"block",width:"100%",padding:12,background:"#25D366",color:"#fff",fontFamily:W.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",fontWeight:700,textAlign:"center",textDecoration:"none",borderRadius:2}}>
              ↗ Consultar por WhatsApp
            </a>
            <div style={{fontFamily:W.sans,fontSize:10,color:W.muted,textAlign:"center",lineHeight:1.5}}>
              También podemos cotizar <strong>otros modelos, colores y tamaños</strong> no mostrados en el catálogo.
            </div>
            {!puedeVerPrecios && (
              <button onClick={()=>navigate("/auth")} style={{width:"100%",padding:10,background:"transparent",color:O,fontFamily:W.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",border:`1.5px solid ${O}`,cursor:"pointer",fontWeight:700}}>
                Ingresar para ver precios →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tarjeta producto ──────────────────────────────────────────────────────────
function TarjetaProducto({producto, onClick, puedeVerPrecios}) {
  const [h, setH] = useState(false);
  const precio = producto.precio_venta;
  const precioIVA = precio ? (precio * 1.21).toFixed(2) : null;
  const esMedida = RUBROS_MEDIDA.includes(producto.rubro);

  return (
    <div onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)} onClick={onClick}
      style={{background:W.s1,border:`1.5px solid ${h?O:W.border}`,cursor:"pointer",transition:"all .2s",overflow:"hidden",boxShadow:h?"0 4px 14px rgba(210,97,26,.1)":"none"}}>
      <div style={{aspectRatio:"4/3",overflow:"hidden",background:W.s2,display:"flex",alignItems:"center",justifyContent:"center"}}>
        {producto.imagen_url
          ? <img src={producto.imagen_url} alt={producto.nombre} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain",transform:h?"scale(1.03)":"scale(1)",transition:"transform .4s"}}/>
          : <div style={{fontFamily:W.sans,fontSize:10,color:W.muted}}>Sin imagen</div>
        }
      </div>
      <div style={{padding:"10px 12px 14px"}}>
        <div style={{fontFamily:W.sans,fontSize:8,letterSpacing:2,textTransform:"uppercase",color:O,marginBottom:3,fontWeight:600}}>{producto.rubro}{esMedida?" · A medida":""}</div>
        <div style={{fontFamily:W.sans,fontSize:"clamp(11px,2vw,13px)",color:BK,fontWeight:700,lineHeight:1.3,marginBottom:6}}>{producto.nombre}</div>
        {puedeVerPrecios
          ? precioIVA
            ? <div style={{fontFamily:W.mono,fontSize:"clamp(11px,2vw,13px)",color:O,fontWeight:700}}>USD {Number(precioIVA).toLocaleString("es-AR",{minimumFractionDigits:2})} / {producto.unidad}</div>
            : <div style={{fontFamily:W.sans,fontSize:9,color:W.muted,textTransform:"uppercase",letterSpacing:1}}>Cotización personalizada</div>
          : <div style={{fontFamily:W.sans,fontSize:10,color:W.muted}}>🔒 Precio para clientes</div>
        }
      </div>
      {h && <div style={{height:2,background:O}}/>}
    </div>
  );
}

// ── Sección de categoría con slider horizontal ────────────────────────────────
function SeccionCategoria({rubro, items, puedeVerPrecios, onProducto, onVerTodos}) {
  const esMedida = RUBROS_MEDIDA.includes(rubro);
  const [expandido, setExpandido] = useState(false);
  const sliderRef = useRef(null);
  const CARD_W = 200;
  const GAP = 12;

  const scroll = (dir) => {
    if (sliderRef.current) sliderRef.current.scrollBy({left: dir * (CARD_W + GAP) * 2, behavior:"smooth"});
  };

  const itemsMostrados = expandido ? items : items;

  return (
    <div style={{marginBottom:40}}>
      {/* Header de categoría */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,paddingBottom:10,borderBottom:`2px solid ${W.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:4,height:28,background:O,flexShrink:0}}/>
          <div>
            <h2 style={{fontFamily:W.sans,fontSize:20,fontWeight:700,color:BK,lineHeight:1.1}}>{rubro}</h2>
            {esMedida && (
              <div style={{fontFamily:W.sans,fontSize:10,color:"#2563EB",fontWeight:600,marginTop:2}}>✦ Fabricación a medida · Trabajo en cooperación con fábrica</div>
            )}
            <div style={{fontFamily:W.mono,fontSize:9,color:W.muted,marginTop:1}}>{items.length} producto{items.length!==1?"s":""} · También fabricamos otros modelos y colores</div>
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexShrink:0}}>
          <button onClick={()=>onVerTodos(rubro)}
            style={{fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",color:O,background:"transparent",border:`1px solid ${O}`,padding:"5px 12px",cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>
            Ver solo {rubro} →
          </button>
        </div>
      </div>

      {/* Slider horizontal */}
      <div style={{position:"relative"}}>
        <div ref={sliderRef} style={{display:"flex",gap:GAP,overflowX:"auto",scrollSnapType:"x mandatory",WebkitOverflowScrolling:"touch",paddingBottom:8,scrollbarWidth:"none",msOverflowStyle:"none"}}>
          {items.map(p => (
            <div key={p.id} style={{flexShrink:0,width:CARD_W,scrollSnapAlign:"start"}}>
              <TarjetaProducto producto={p} puedeVerPrecios={puedeVerPrecios} onClick={()=>onProducto(p)}/>
            </div>
          ))}
          {/* Card final — consultar */}
          <div style={{flexShrink:0,width:CARD_W,scrollSnapAlign:"start"}} onClick={()=>window.open(`https://wa.me/5491150614489?text=${encodeURIComponent(`Hola, me interesa consultar sobre ${rubro}. ¿Tienen otros modelos o colores disponibles?`)}`,"_blank")}>
            <div style={{height:"100%",minHeight:240,background:"#FEF3E8",border:`1.5px dashed ${O}`,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,padding:16,textAlign:"center"}}>
              <div style={{fontSize:28}}>💬</div>
              <div style={{fontFamily:W.sans,fontSize:12,color:O,fontWeight:700,lineHeight:1.3}}>¿No encontrás lo que buscás?</div>
              <div style={{fontFamily:W.sans,fontSize:10,color:W.muted,lineHeight:1.5}}>Consultanos otros modelos, colores y tamaños disponibles</div>
              <div style={{fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",color:O,border:`1px solid ${O}`,padding:"5px 12px",fontWeight:700}}>Consultar →</div>
            </div>
          </div>
        </div>
        {/* Controles scroll */}
        {items.length > 3 && (
          <>
            <button onClick={()=>scroll(-1)} style={{position:"absolute",left:-16,top:"40%",transform:"translateY(-50%)",background:BK,color:"#fff",border:"none",width:32,height:32,cursor:"pointer",fontSize:16,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 8px rgba(0,0,0,.2)"}}>‹</button>
            <button onClick={()=>scroll(1)} style={{position:"absolute",right:-16,top:"40%",transform:"translateY(-50%)",background:BK,color:"#fff",border:"none",width:32,height:32,cursor:"pointer",fontSize:16,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 8px rgba(0,0,0,.2)"}}>›</button>
          </>
        )}
      </div>

      {/* Badge a medida */}
      {esMedida && (
        <div style={{marginTop:12,background:"#EFF6FF",border:"1px solid #2563EB33",padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontFamily:W.sans,fontSize:11,color:"#1D4ED8",lineHeight:1.5}}>
            <strong>✦ {rubro} a medida:</strong> Trabajamos en cooperación con la fábrica. Podemos hacer cualquier diseño, medida o color según tus planos.
          </div>
          <a href={`https://wa.me/5491150614489?text=${encodeURIComponent(`Hola, me interesa cotizar ${rubro} a medida para mi proyecto.`)}`} target="_blank" rel="noreferrer"
            style={{padding:"8px 16px",background:"#2563EB",color:"#fff",fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>
            Enviar mis planos →
          </a>
        </div>
      )}
    </div>
  );
}

// ── PÁGINA ────────────────────────────────────────────────────────────────────
export default function CatalogoPublico() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const puedeVerPrecios = !!(user && profile && profile.estado === "activo");

  const [rubro, setRubro] = useState(() => {
    try { const r = new URLSearchParams(window.location.search).get("rubro"); return r ? decodeURIComponent(r) : null; }
    catch { return null; }
  });
  const [productoIdInicial] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("producto"); } catch { return null; }
  });

  useEffect(() => {
    getDocs(collection(db,"productos"))
      .then(snap => {
        // Filtrar publicados en el cliente (evita query compuesta que requiere índice)
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.publicado !== false); // incluye publicado=true y publicado=undefined
        items.sort((a, b) => (a.orden || 999) - (b.orden || 999) || (a.nombre || "").localeCompare(b.nombre || ""));
        setProductos(items);
        if (productoIdInicial) { const p = items.find(x => x.id === productoIdInicial); if (p) setDetalle(p); }
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [productoIdInicial]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const cambiarRubro = useCallback((r) => {
    setRubro(r);
    const p = {}; if (r) p.rubro = r;
    setSearchParams(p, { replace: true });
  }, [setSearchParams]);

  const RUBROS_ACTIVOS = ["Sanitarios","Bañeras","Vanitorys","Pisos SPC","Hornos y Anafes","Puertas","Carpintería","Cocinas","Barandas y Balcones"];
  const rubrosConProductos = RUBROS_ACTIVOS.filter(r => productos.some(p => p.rubro === r));
  const counts = { total: productos.length, ...RUBROS_ACTIVOS.reduce((a,r) => ({...a,[r]:productos.filter(p=>p.rubro===r).length}),{}) };

  const filtrados = productos.filter(p => {
    const mR = rubro === null || p.rubro === rubro;
    const mB = !busqueda || p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) || p.rubro?.toLowerCase().includes(busqueda.toLowerCase());
    return mR && mB;
  });

  const porCategorias = rubrosConProductos.map(r => ({ rubro: r, items: filtrados.filter(p => p.rubro === r) })).filter(g => g.items.length > 0);

  return (
    <div style={{background:W.bg,minHeight:"100vh",fontFamily:W.sans}}>
      <style>{FONTS}</style>

      {/* NAV */}
      <nav className="cat-nav" style={{position:"sticky",top:0,zIndex:100,background:scrolled?"rgba(250,249,247,.97)":"#FAF9F7",backdropFilter:"blur(10px)",borderBottom:`1px solid ${W.border}`,display:"flex",alignItems:"center",padding:"0 20px",height:56,gap:10}}>
        <div onClick={()=>navigate("/")} style={{cursor:"pointer",marginRight:"auto"}}><LogoWinarq height={34} dark={true}/></div>
        <input value={busqueda} onChange={e=>setBusqueda(e.target.value)} placeholder="Buscar productos..."
          style={{width:160,background:W.s1,border:`1.5px solid ${W.border}`,color:BK,fontFamily:W.sans,fontSize:13,padding:"6px 10px",outline:"none"}}/>
        {!puedeVerPrecios && (
          <button onClick={()=>navigate("/auth")} style={{padding:"7px 12px",background:O,color:"#fff",fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",border:"none",cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>Ver precios →</button>
        )}
        <button onClick={()=>navigate("/auth")} style={{padding:"7px 12px",background:BK,color:"#fff",fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",border:"none",cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>
          {user ? "Mi cuenta" : "Ingresar"}
        </button>
      </nav>

      {/* HEADER */}
      <div style={{padding:"16px 20px 0",borderBottom:`1px solid ${W.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontFamily:W.sans,fontSize:9,color:O,letterSpacing:2,textTransform:"uppercase",marginBottom:3,fontWeight:600}}>{rubro || "Catálogo completo"}</div>
            <h1 style={{fontFamily:W.sans,fontSize:"clamp(16px,4vw,26px)",fontWeight:700,color:BK}}>{rubro || "Todos los productos"}</h1>
            <div style={{fontFamily:W.mono,fontSize:9,color:W.muted,marginTop:2}}>{filtrados.length} productos · {puedeVerPrecios ? "Precios con IVA" : "Registrate para ver precios"}</div>
          </div>
          {!puedeVerPrecios && (
            <div style={{background:"#FEF3E8",border:`1px solid ${O}44`,padding:"8px 14px",fontFamily:W.sans,fontSize:11,color:W.off,lineHeight:1.5,alignSelf:"flex-start"}}>
              🔒 <strong>Precios ocultos.</strong>{" "}
              <span style={{color:O,cursor:"pointer",fontWeight:600}} onClick={()=>navigate("/auth")}>Ingresar para ver →</span>
            </div>
          )}
        </div>
        {/* Chips */}
        <div className="cat-chips" style={{display:"flex",gap:6,paddingBottom:12,overflowX:"auto"}}>
          <button className="cat-chip" onClick={()=>cambiarRubro(null)}
            style={{padding:"4px 12px",fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",background:rubro===null?O:W.s1,color:rubro===null?"#fff":W.muted,border:`1.5px solid ${rubro===null?O:W.border}`,cursor:"pointer",fontWeight:rubro===null?700:500}}>
            Todos ({counts.total})
          </button>
          {rubrosConProductos.map(r => (
            <button key={r} className="cat-chip" onClick={()=>cambiarRubro(rubro===r?null:r)}
              style={{padding:"4px 12px",fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",background:rubro===r?O:W.s1,color:rubro===r?"#fff":W.muted,border:`1.5px solid ${rubro===r?O:W.border}`,cursor:"pointer",fontWeight:rubro===r?700:500}}>
              {r} ({counts[r]||0})
            </button>
          ))}
        </div>
      </div>

      {/* LAYOUT */}
      <div className="cat-layout" style={{display:"flex",gap:20,padding:"16px 20px"}}>
        {/* Sidebar */}
        <div className="cat-sidebar" style={{width:180,flexShrink:0}}>
          <div className="cat-sidebar-lbl" style={{fontFamily:W.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:W.muted,marginBottom:8,fontWeight:600}}>Categorías</div>
          <button onClick={()=>cambiarRubro(null)}
            style={{width:"100%",display:"flex",justifyContent:"space-between",padding:"8px 10px",marginBottom:3,background:rubro===null?O:W.s1,color:rubro===null?"#fff":W.off,border:`1px solid ${rubro===null?O:W.border}`,cursor:"pointer",fontFamily:W.sans,fontSize:11,fontWeight:rubro===null?700:400,textAlign:"left"}}>
            <span>Todos</span><span style={{opacity:.6,fontFamily:W.mono,fontSize:10}}>{counts.total}</span>
          </button>
          {rubrosConProductos.map(r => (
            <button key={r} onClick={()=>cambiarRubro(rubro===r?null:r)}
              style={{width:"100%",display:"flex",justifyContent:"space-between",padding:"8px 10px",marginBottom:3,background:rubro===r?O:W.s1,color:rubro===r?"#fff":W.off,border:`1px solid ${rubro===r?O:W.border}`,cursor:"pointer",fontFamily:W.sans,fontSize:11,fontWeight:rubro===r?700:400,textAlign:"left"}}>
              <span>{r}</span><span style={{opacity:.6,fontFamily:W.mono,fontSize:10}}>{counts[r]||0}</span>
            </button>
          ))}
        </div>

        {/* Contenido */}
        <div style={{flex:1,minWidth:0}}>
          {loading ? (
            <div style={{padding:40,textAlign:"center",fontFamily:W.sans,fontSize:12,color:W.muted}}>Cargando...</div>
          ) : rubro !== null || busqueda ? (
            /* Vista filtrada — grid */
            filtrados.length === 0 ? (
              <div style={{padding:40,textAlign:"center"}}>
                <div style={{fontFamily:W.sans,fontSize:13,color:W.muted,marginBottom:14}}>Sin productos en esta categoría.</div>
                <button onClick={()=>cambiarRubro(null)} style={{padding:"9px 18px",background:O,color:"#fff",fontFamily:W.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",border:"none",cursor:"pointer",fontWeight:700}}>Ver todos →</button>
              </div>
            ) : (
              <>
                <div className="cat-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:12}}>
                  {filtrados.map(p => <TarjetaProducto key={p.id} producto={p} puedeVerPrecios={puedeVerPrecios} onClick={()=>setDetalle(p)}/>)}
                </div>
                {/* CTA consultar más */}
                <div style={{marginTop:20,padding:"14px 18px",background:"#FEF3E8",border:`1px solid ${O}44`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
                  <div style={{fontFamily:W.sans,fontSize:12,color:W.off,lineHeight:1.5}}>
                    <strong>¿No encontrás el modelo o color que buscás?</strong><br/>
                    Tenemos acceso a muchos más modelos y podemos cotizar cualquier especificación.
                  </div>
                  <a href={`https://wa.me/5491150614489?text=${encodeURIComponent(`Hola, busco ${rubro||"materiales"} y no encontré lo que necesito en el catálogo. ¿Pueden ayudarme?`)}`}
                    target="_blank" rel="noreferrer"
                    style={{padding:"9px 18px",background:O,color:"#fff",fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>
                    Consultar →
                  </a>
                </div>
              </>
            )
          ) : (
            /* Vista todos — sliders por categoría */
            porCategorias.map(({rubro:r,items}) => (
              <SeccionCategoria
                key={r} rubro={r} items={items}
                puedeVerPrecios={puedeVerPrecios}
                onProducto={setDetalle}
                onVerTodos={cambiarRubro}
              />
            ))
          )}
        </div>
      </div>

      {/* Banner cotización */}
      <div style={{margin:"0 20px 20px",padding:"18px 20px",background:BK,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontFamily:W.sans,fontSize:15,color:"#fff",fontWeight:700,marginBottom:3}}>
            Podemos cotizar lo que sea — incluso si no está en el catálogo
          </div>
          <div style={{fontFamily:W.sans,fontSize:11,color:"rgba(255,255,255,.45)"}}>
            Acceso directo a fábricas en China · Cualquier producto · Cualquier especificación
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <a href="https://wa.me/5491150614489" target="_blank" rel="noreferrer"
            style={{padding:"9px 14px",background:"transparent",color:O,fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",border:`1.5px solid ${O}`,fontWeight:700,textDecoration:"none",display:"flex",alignItems:"center",whiteSpace:"nowrap"}}>
            ↗ WhatsApp
          </a>
          <button onClick={()=>navigate("/auth")} style={{padding:"9px 14px",background:O,color:"#fff",fontFamily:W.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",border:"none",cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>
            {puedeVerPrecios ? "Cotizar mi obra →" : "Ver precios →"}
          </button>
        </div>
      </div>

      {detalle && <DetalleProducto producto={detalle} onClose={()=>setDetalle(null)} puedeVerPrecios={puedeVerPrecios}/>}
    </div>
  );
}
